import { useEffect, useRef } from 'react';
import { useLocation } from 'react-router-dom';
import { chatPermitido } from '@/lib/chat';
import { env } from '@/lib/env';

/**
 * Chat do site — Centriu Link.
 *
 * COMO ELE ENTRA NA PÁGINA
 * O widget é um script de terceiro que se pendura no `document.body`, fora da
 * árvore do React, dentro de um Shadow DOM próprio. Ou seja: o React monta
 * este componente, mas NÃO manda no elemento do chat depois — desmontar o
 * componente não tira o chat da tela.
 *
 * É por isso que a saída deste componente esconde o widget em vez de confiar
 * na desmontagem. Sem isso, o chat continuaria visível no comprovante e no
 * painel administrativo, que ficam fora do Layout.
 *
 * ONDE ELE APARECE
 * A regra está em `@/lib/chat`, com teste. Resumo: em toda página pública,
 * inclusive carrinho e checkout; em nenhuma página sob `/pedido` ou `/admin`.
 * O motivo está escrito lá e é o que importa deste arquivo inteiro.
 *
 * QUANDO ELE CARREGA
 * Depois que o navegador fica ocioso, nunca no caminho crítico. O primeiro
 * carregamento do site já disputa banda com a fonte, o pacote da aplicação e
 * a chamada do catálogo; 57 KB de chat na frente disso atrasaria o texto que
 * o visitante veio ler. `requestIdleCallback` com teto de 3 segundos garante
 * que ele entra mesmo em máquina que nunca fica realmente ociosa.
 *
 * PARA DESLIGAR
 * Deixe `VITE_CENTRIU_ORG_ID` vazio no ambiente. Útil em homologação, para não
 * misturar conversa de teste com atendimento de verdade no painel do Centriu.
 */

interface CentriuChat {
  organizationId: string;
  widgetToken: string;
  title?: string;
  subtitle?: string;
  welcome?: string;
  position?: 'bottom-right' | 'bottom-left';
  color?: string;
  launcherLabel?: string;
  privacyUrl?: string;
  /* Métodos publicados pelo script depois que ele sobe. */
  open?: () => void;
  close?: () => void;
  toggle?: () => void;
}

declare global {
  interface Window {
    CentriuChat?: CentriuChat;
  }
}

const SRC = 'https://link.centriu.com/widget.js';

/**
 * Classe no `<body>` que esconde o widget. O seletor mora em `src/index.css`
 * porque precisa alcançar `[data-centriu-chat]`, que é criado fora do React.
 */
const CLASSE_OCULTO = 'chat-oculto';

export default function ChatWidget() {
  const { pathname } = useLocation();
  const injetado = useRef(false);

  const ligado = env.VITE_CENTRIU_ORG_ID !== '' && env.VITE_CENTRIU_WIDGET_TOKEN !== '';
  const permitido = ligado && chatPermitido(pathname);

  /* ------------------------------------------------ 1. Injeção, uma única vez */
  useEffect(() => {
    if (!permitido || injetado.current) return;

    let cancelado = false;

    function injetar() {
      /*
        A marca fica DENTRO da função, não fora: em modo estrito o React monta,
        desmonta e monta de novo em desenvolvimento. Marcar antes de agendar
        faria a segunda montagem achar que já tinha injetado, e o chat nunca
        apareceria na máquina de quem desenvolve.
      */
      if (cancelado || injetado.current) return;
      if (document.querySelector(`script[src="${SRC}"]`)) return;
      injetado.current = true;

      /*
        A configuração precisa existir ANTES do script: ele lê
        `window.CentriuChat` na primeira linha e desiste com um aviso no console
        se o identificador da organização estiver faltando.
      */
      window.CentriuChat = {
        organizationId: env.VITE_CENTRIU_ORG_ID,
        widgetToken: env.VITE_CENTRIU_WIDGET_TOKEN,
        title: 'Fale com o laboratório',
        subtitle: 'Atendimento de segunda a sábado, em horário comercial',
        /*
          A mensagem de abertura faz um trabalho que nenhuma outra tela faz:
          desviar o pedido de RESULTADO para o portal. Resultado de exame não
          sai do sistema laboratorial (D-06) e não pode trafegar por um chat de
          terceiro. Dizer isso na primeira linha é mais barato do que corrigir
          depois, uma conversa por vez.
        */
        welcome:
          'Olá! Posso ajudar com preço de exame, preparo, jejum, unidades e horários. ' +
          'Resultado de exame só pelo portal Resultados — por segurança, não enviamos ' +
          'resultado pelo chat.',
        position: 'bottom-right',
        color: '#0E725B',
        launcherLabel: 'Fale conosco',
        /*
          O widget transforma isto no link "privacidade" do rodapé do painel.
          É exigência prática da LGPD: o visitante precisa conseguir chegar à
          política a partir do lugar onde ele está prestes a digitar dado
          pessoal.
        */
        privacyUrl: '/privacidade',
      };

      const script = document.createElement('script');
      script.src = SRC;
      script.async = true;
      document.head.appendChild(script);
    }

    const agendar = window.requestIdleCallback;
    if (typeof agendar === 'function') {
      const id = agendar(injetar, { timeout: 3000 });
      return () => {
        cancelado = true;
        window.cancelIdleCallback?.(id);
      };
    }

    const id = window.setTimeout(injetar, 1500);
    return () => {
      cancelado = true;
      window.clearTimeout(id);
    };
  }, [permitido]);

  /* ------------------------------- 2. Esconde ao sair do Layout (comprovante,
                                        painel) — o widget não desmonta sozinho */
  useEffect(() => {
    return () => {
      document.body.classList.add(CLASSE_OCULTO);
      window.CentriuChat?.close?.();
    };
  }, []);

  /* --------------------------------------- 3. Mostra ou esconde por rota */
  useEffect(() => {
    document.body.classList.toggle(CLASSE_OCULTO, !permitido);

    /*
      Fechar o painel além de escondê-lo importa: sem isso, quem estivesse com
      a conversa aberta e clicasse para acompanhar o pedido veria o chat sumir
      e reapareceria aberto ao voltar, sobre uma página onde ele não devia
      estar.
    */
    if (!permitido) window.CentriuChat?.close?.();
  }, [permitido]);

  return null;
}
